import React from 'react';
import { getOr, isNumber, omit } from 'lodash/fp';
import PropTypes from 'prop-types';
import {
  Grid,
  Row,
  Col,
  Icon,
  Input,
  DateRangePicker,
  Button,
  SelectPicker,
  InputGroup
} from 'rsuite';

const ReservationSearch = ({
  queryOptions,
  searchQuery,
  placeholder,
  onChange,
  onSubmit,
  onReset,
  isFetching,
  isLegacyView,
  statusOptions
}) => {
  const { q: query } = queryOptions;

  const handleOnChange = q => {
    if (q) onChange({ ...searchQuery, q });
    else onChange(omit('q', searchQuery));
  };

  const handleDateRangeChange = value => {
    const updatedQuery = { ...searchQuery, requestTime: value };
    onChange(updatedQuery);
    onSubmit({ ...queryOptions, ...updatedQuery });
  };

  const handleOnReset = () => {
    const querySearch = omit(['status', 'q'], searchQuery);
    onReset({ ...querySearch, requestTime: [] });
  };

  const handleBarcodeChange = code => {
    if (code) onChange({ ...searchQuery, code });
    else onChange(omit('code', searchQuery));
  };

  const handleOnChangeStatus = status => {
    if (isNumber(status)) {
      const updatedQuery = { ...searchQuery, status };
      onChange(updatedQuery);
      onSubmit(updatedQuery);
    } else {
      const updated = omit('status', searchQuery);
      onChange(updated);
      onSubmit(updated);
    }
  };

  return (
    <Grid fluid style={{ padding: 0, marginLeft: 0 }}>
      <Row gutter={8}>
        <Col xs={24} md={isLegacyView ? 10 : 4}>
          <InputGroup>
            <Input
              value={query}
              onChange={handleOnChange}
              style={{ paddingLeft: 10 }}
              placeholder={placeholder}
              disabled={isFetching}
              onKeyPress={({ key }) => {
                if (key === 'Enter') onSubmit(queryOptions);
              }}
            />
            <InputGroup.Button
              style={{ borderBottom: 'none' }}
              disabled={!query || isFetching}
              onClick={() => {
                if (query !== '') onSubmit(queryOptions);
              }}
            >
              <Icon icon="search" />
            </InputGroup.Button>
          </InputGroup>
        </Col>
        <Col xs={24} md={isLegacyView ? 6 : 3}>
          <InputGroup>
            <Input
              value={getOr('', 'code', queryOptions)}
              onChange={handleBarcodeChange}
              style={{ paddingLeft: 10 }}
              placeholder="Search barcode"
              disabled={isFetching}
              onKeyPress={({ key }) => {
                if (key === 'Enter') onSubmit(queryOptions);
              }}
            />
            <InputGroup.Button
              style={{ borderBottom: 'none' }}
              disabled={!query || isFetching}
              onClick={() => {
                if (query !== '') onSubmit(queryOptions);
              }}
            >
              <Icon icon="search" />
            </InputGroup.Button>
          </InputGroup>
        </Col>
        <Col xs={24} md={isLegacyView ? 10 : 4}>
          <DateRangePicker
            style={{ width: '100%', marginRight: 10 }}
            value={getOr([], 'requestTime', queryOptions)}
            placeholder="Select Date Range"
            onChange={handleDateRangeChange}
            disabled={isFetching}
          />
        </Col>
        <Col xs={24} md={isLegacyView ? 6 : 3}>
          <SelectPicker
            style={{ width: '100%', marginRight: 10 }}
            value={getOr('', 'status', queryOptions)}
            data={statusOptions}
            appearance="default"
            placeholder="Status"
            onChange={handleOnChangeStatus}
          />
        </Col>
        <Col xs={24} md={4}>
          <Button
            onClick={handleOnReset}
            disabled={isFetching}
            appearance="ghost"
          >
            <Icon icon="refresh" /> Reset
          </Button>
        </Col>
      </Row>
    </Grid>
  );
};

const StatusOption = PropTypes.shape({
  value: PropTypes.number,
  label: PropTypes.string
});

ReservationSearch.propTypes = {
  placeholder: PropTypes.string.isRequired,
  searchQuery: PropTypes.shape({
    q: PropTypes.string,
    client_id: PropTypes.number,
    visit_on: PropTypes.arrayOf(PropTypes.shape()),
    location_id: PropTypes.arrayOf(PropTypes.number)
  }),
  queryOptions: PropTypes.shape({
    q: PropTypes.string,
    client_id: PropTypes.number,
    visit_on: PropTypes.arrayOf(PropTypes.shape({})),
    location_id: PropTypes.arrayOf(PropTypes.number),
    limit: PropTypes.number,
    page: PropTypes.number,
    order: PropTypes.string
  }),
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  isLegacyView: PropTypes.bool,
  statusOptions: PropTypes.arrayOf(StatusOption).isRequired
};

ReservationSearch.defaultProps = {
  searchQuery: {},
  queryOptions: {},
  isLegacyView: false
};

export default ReservationSearch;
